import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, User, Phone, MapPin, BadgeCheck, Upload, X, FileText, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { FormPageShell } from "../components/FormPageShell";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";
const MAX_DOC_BYTES = 5 * 1024 * 1024;
const ACCEPT_DOCS = "image/jpeg,image/png,image/webp,image/heic,application/pdf";

const isPdfFile = (file) =>
  !!file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));

const resetFileInput = (id) => {
  const input = document.getElementById(id);
  if (input) input.value = "";
};

const DocUpload = ({ id, label, required, optionalLabel, hint, formatsHint, file, preview, onPick, onClear }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>
      {label} {required ? "*" : <span className="text-slate-400 font-normal">{optionalLabel}</span>}
    </Label>
    <div
      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
        file ? "border-omu-blue-500 bg-omu-blue-50" : "border-slate-200 hover:border-omu-blue-500"
      }`}
      onClick={() => document.getElementById(id)?.click()}
      data-testid={`${id}-zone`}
    >
      {file && preview && !isPdfFile(file) ? (
        <div className="relative">
          <img src={preview} alt="" className="max-h-40 mx-auto rounded-lg" />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-0 right-0 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              resetFileInput(id);
              onClear();
            }}
          >
            <X className="h-4 w-4" />
          </Button>
          <p className="text-xs text-slate-500 mt-2 truncate">{file.name}</p>
        </div>
      ) : file ? (
        <div className="relative">
          <FileText className="h-10 w-10 mx-auto text-omu-blue-500" />
          <p className="text-slate-700 font-medium mt-2 truncate">{file.name}</p>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-0 right-0 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              resetFileInput(id);
              onClear();
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <Upload className="h-9 w-9 mx-auto text-slate-400" />
          <p className="text-slate-600 font-medium mt-2">{hint}</p>
          <p className="text-sm text-slate-400">{formatsHint}</p>
        </>
      )}
      <input
        id={id}
        type="file"
        accept={ACCEPT_DOCS}
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={id}
      />
    </div>
  </div>
);

const DoctorRegisterPage = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    postnom: "",
    prenom: "",
    numero_ordre: "",
    phone: "",
    city: "",
    email: "",
    password: "",
    confirmPassword: "",
    identity_type: "",
  });
  const [cnomNotification, setCnomNotification] = useState(null);
  const [cnomNotificationPreview, setCnomNotificationPreview] = useState(null);
  const [identityDoc, setIdentityDoc] = useState(null);
  const [identityPreview, setIdentityPreview] = useState(null);
  const [cnomCard, setCnomCard] = useState(null);
  const [cnomCardPreview, setCnomCardPreview] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const pickDoc = (file, setter, previewSetter) => {
    if (!file) return;
    if (file.size > MAX_DOC_BYTES) {
      toast.error(t("fileTooLarge"));
      return;
    }
    setter(file);
    if (isPdfFile(file)) {
      previewSetter(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => previewSetter(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom || !formData.postnom || !formData.prenom || !formData.numero_ordre || !formData.email || !formData.password) {
      toast.error(t("fillRequiredFields"));
      return;
    }
    if (!formData.identity_type) {
      toast.error(t("identityTypeRequired"));
      return;
    }
    if (!cnomNotification) {
      toast.error(t("cnomNotificationRequired"));
      return;
    }
    if (!identityDoc) {
      toast.error(t("identityDocRequired"));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error(t("passwordsMismatch"));
      return;
    }
    if (formData.password.length < 6) {
      toast.error(t("passwordMinLength"));
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("nom", formData.nom);
      payload.append("postnom", formData.postnom);
      payload.append("prenom", formData.prenom);
      payload.append("numero_ordre", formData.numero_ordre);
      payload.append("email", formData.email);
      payload.append("password", formData.password);
      payload.append("identity_type", formData.identity_type);
      if (formData.phone) payload.append("phone", formData.phone);
      if (formData.city) payload.append("city", formData.city);
      payload.append("cnom_notification", cnomNotification);
      payload.append("identity_doc", identityDoc);
      if (cnomCard) payload.append("cnom_card_recto", cnomCard);

      const response = await apiClient.post("/auth/register/doctor", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      localStorage.setItem("omu_token", response.data.access_token);
      setToken(response.data.access_token);
      setUser(response.data.user);
      toast.success(t("registerDoctorSuccess"));
      navigate("/doctor/prescriptions");
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormPageShell centered={false}>
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="OMU" className="h-20 w-auto mx-auto" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">
            {t("registerDoctorTitle")}
          </h1>
          <p className="text-slate-500 mt-2">
            {t("registerDoctorSubtitle")}
          </p>
        </div>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nom">{t("lastName")}</Label>
                  <Input id="nom" name="nom" value={formData.nom} onChange={handleInputChange} className="h-12 rounded-xl" data-testid="doctor-nom" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postnom">{t("middleName")}</Label>
                  <Input id="postnom" name="postnom" value={formData.postnom} onChange={handleInputChange} className="h-12 rounded-xl" data-testid="doctor-postnom" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prenom">{t("firstName")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input id="prenom" name="prenom" value={formData.prenom} onChange={handleInputChange} className="h-12 rounded-xl pl-10" data-testid="doctor-prenom" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="numero_ordre">{t("orderNumber")}</Label>
                <div className="relative">
                  <BadgeCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input id="numero_ordre" name="numero_ordre" placeholder={t("orderNumberPlaceholder")} value={formData.numero_ordre} onChange={handleInputChange} className="h-12 rounded-xl pl-10" data-testid="doctor-cnom" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t("email")} *</Label>
                <Input id="email" name="email" type="email" placeholder={t("emailPlaceholder")} value={formData.email} onChange={handleInputChange} className="h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t("phone")}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input id="phone" name="phone" type="tel" placeholder={t("phonePlaceholder")} value={formData.phone} onChange={handleInputChange} className="h-12 rounded-xl pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">{t("cityOptional")}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input id="city" name="city" placeholder={t("cityPlaceholder")} value={formData.city} onChange={handleInputChange} className="h-12 rounded-xl pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("password")} *</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? "text" : "password"} placeholder={t("passwordPlaceholder")} value={formData.password} onChange={handleInputChange} className="h-12 rounded-xl pr-10" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmPassword")} *</Label>
                <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="••••••••" value={formData.confirmPassword} onChange={handleInputChange} className="h-12 rounded-xl" />
              </div>

              <div className="pt-2 space-y-4">
                <h2 className="font-heading text-lg font-semibold">{t("doctorVerificationDocs")}</h2>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t("doctorDocsAlert")}
                  </AlertDescription>
                </Alert>

                <DocUpload
                  id="cnom-notification"
                  label={t("cnomNotification")}
                  required
                  optionalLabel={t("optional")}
                  hint={t("cnomNotificationHint")}
                  formatsHint={t("docUploadFormats")}
                  file={cnomNotification}
                  preview={cnomNotificationPreview}
                  onPick={(file) => pickDoc(file, setCnomNotification, setCnomNotificationPreview)}
                  onClear={() => {
                    setCnomNotification(null);
                    setCnomNotificationPreview(null);
                  }}
                />

                <div className="space-y-2">
                  <Label>{t("identityType")}</Label>
                  <Select
                    value={formData.identity_type || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, identity_type: value }))}
                  >
                    <SelectTrigger className="h-12 rounded-xl" data-testid="doctor-identity-type">
                      <SelectValue placeholder={t("identityTypeSelect")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="electeur">{t("identityTypeElecteur")}</SelectItem>
                      <SelectItem value="passeport">{t("identityTypePassport")}</SelectItem>
                      <SelectItem value="cni">{t("identityTypeCni")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <DocUpload
                  id="identity-doc"
                  label={t("identityDoc")}
                  required
                  optionalLabel={t("optional")}
                  hint={t("identityDocHint")}
                  formatsHint={t("docUploadFormats")}
                  file={identityDoc}
                  preview={identityPreview}
                  onPick={(file) => pickDoc(file, setIdentityDoc, setIdentityPreview)}
                  onClear={() => {
                    setIdentityDoc(null);
                    setIdentityPreview(null);
                  }}
                />

                <DocUpload
                  id="cnom-card-recto"
                  label={t("cnomCard")}
                  required={false}
                  optionalLabel={t("optional")}
                  hint={t("cnomCardHint")}
                  formatsHint={t("docUploadFormats")}
                  file={cnomCard}
                  preview={cnomCardPreview}
                  onPick={(file) => pickDoc(file, setCnomCard, setCnomCardPreview)}
                  onClear={() => {
                    setCnomCard(null);
                    setCnomCardPreview(null);
                  }}
                />
              </div>

              <Button type="submit" className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600" disabled={loading} data-testid="doctor-register-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("createDoctorAccount")}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          {t("alreadySignedIn")}{" "}
          <Link to="/login" className="text-omu-blue-500 font-medium hover:underline">{t("signIn")}</Link>
        </p>
        <p className="text-center text-sm text-slate-500 mt-2">
          {t("patientPrompt")}{" "}
          <Link to="/register/client" className="text-omu-blue-500 font-medium hover:underline">{t("patientRegister")}</Link>
        </p>
        <p className="text-center text-sm text-slate-400 mt-4">
          <Link to="/" className="hover:text-slate-600">{tc("backHome")}</Link>
        </p>
      </div>
    </FormPageShell>
  );
};

export default DoctorRegisterPage;
